import { forwardRef } from 'react';
import styled from 'styled-components';
import { themeColors, themeSizes } from '../../styles/globalStyleVars';
import {
  TypographyVariant,
  FontWeight,
  TextAlign,
  TypographyStyledProps,
} from './types/TypographyTypes';
import {
  variantMapping,
  fontWeightMapping,
  variantDefaultFontWeight,
  variantDefaultLineHeight,
} from './types/TypographyConstants';
import { getVariantElement } from '../../utils/TypographyHelpers';

// Base styled component that applies defaults via CSS custom properties
// This allows styled-components to override without !important
// The component uses CSS custom properties for fontSize, fontWeight, lineHeight, and color
// which can be overridden by styled-components
const TypographyBase = styled.p
  .withConfig({
    shouldForwardProp: prop =>
      ![
        'variant',
        'fontWeight',
        'color',
        'align',
        'gutterBottom',
        'disabled',
      ].includes(prop),
  })
  .attrs<{
    variant?: TypographyVariant;
    fontWeight?: FontWeight;
    color?: string;
    align?: TextAlign;
    as?: React.ElementType;
    gutterBottom?: boolean;
    id?: string;
    disabled?: boolean;
  }>(({ variant = 'body1', fontWeight, color, disabled }) => {
    // Use provided fontWeight, or fall back to variant's default fontWeight
    const finalFontWeight = fontWeight || variantDefaultFontWeight[variant];
    // Use variant's default line height
    const lineHeight = variantDefaultLineHeight[variant];
    // If disabled, use #a7acaf, otherwise use provided color or default
    const finalColor = disabled
      ? themeColors.colorGray_3
      : color || themeColors.colorGray_6;

    return {
      style: {
        '--typography-font-size': variantMapping[variant],
        '--typography-font-weight': fontWeightMapping[finalFontWeight],
        '--typography-line-height': lineHeight,
        '--typography-color': finalColor,
      } as React.CSSProperties,
    };
  })<
  {
    variant?: TypographyVariant;
    fontWeight?: FontWeight;
    color?: string;
    align?: TextAlign;
    as?: React.ElementType;
    gutterBottom?: boolean;
    id?: string;
    disabled?: boolean;
  } & React.HTMLAttributes<HTMLElement>
>`
  margin: 0;
  font-size: var(--typography-font-size);
  font-weight: var(--typography-font-weight);
  line-height: var(--typography-line-height);
  color: var(--typography-color);
  ${props => props.align && `text-align: ${props.align};`}
  ${props => props.gutterBottom && `margin-bottom: ${themeSizes.md};`}
`;

// TypographyStyled maintains the original API and can be extended
// This allows styled(TypographyStyled) to override styles without !important
export const TypographyStyled = forwardRef<HTMLElement, TypographyStyledProps>(
  (
    {
      text = '',
      variant = 'body1',
      fontWeight,
      color = themeColors.colorGray_6,
      align,
      style,
      className,
      children,
      as,
      gutterBottom,
      id,
      disabled,
      ...rest
    },
    ref
  ) => {
    // Use provided 'as' prop, or default to the element for the variant
    const element = as || getVariantElement(variant);

    return (
      <TypographyBase
        ref={ref}
        className={className}
        variant={variant}
        fontWeight={fontWeight}
        color={color}
        align={align}
        style={style}
        as={element}
        gutterBottom={gutterBottom}
        id={id}
        disabled={disabled}
        {...rest}
      >
        {children || text}
      </TypographyBase>
    );
  }
);

TypographyStyled.displayName = 'TypographyStyled';

export { TypographyBase };
export type { TypographyStyledProps } from './types/TypographyTypes';
export {
  getFontSize,
  getFontWeight,
  getVariantElement,
} from '../../utils/TypographyHelpers';
